<script lang="ts" setup>
  import { useToast } from '@travelata-libs/ui-kit';
  import { LeadBanner, type LeadBannerPayload } from '@travelata-libs/ui-shared';
  import { useIntersectionObserver } from '@vueuse/core';
  import { ref, watch, computed } from 'vue';
  import { yandexTrackingService } from '@/entities/analytics';
  import { createLead } from '@/shared/api/createLead';
  import { useTravelataAppConfig } from '@/shared/composables';
  import { createUuid } from '@/shared/lib/createUuid';
  import { LEAD_BANNER_TEXTS } from './config/texts';
  import { setLeadCookie, checkLeadSession } from './lib';

  interface LeadBannerProps {
    title: string;
    subtitle: string;
    mobile?: boolean;
    modalTitle?: string;
  }

  withDefaults(defineProps<LeadBannerProps>(), {
    modalTitle: undefined,
    mobile: false,
  });

  const cardId = createUuid();

  const formRef = ref<InstanceType<typeof LeadBanner> | null>(null);
  const { showToast } = useToast();
  const appConfig = useTravelataAppConfig();

  const isSubmitted = ref<boolean>(checkLeadSession());
  const loading = ref<boolean>(false);
  const model = ref<LeadBannerPayload>({ phone: '', name: '' });
  let formStart = false;

  const targetEl = computed(() => (formRef.value?.$el ?? undefined) as HTMLElement | undefined);

  const onSubmit = async (data: LeadBannerPayload) => {
    isSubmitted.value = false;
    loading.value = true;
    try {
      await createLead(data);
      setLeadCookie();
      isSubmitted.value = true;
      formRef.value?.closeModal();
      yandexTrackingService.setTrackingGoal('assistantSalesFormSubmit', 'click', {
        placement: 'main_page',
        card_id: cardId,
      });
    } catch {
      showToast({
        type: 'error',
        title: 'Что-то пошло не так',
        description: 'Заявка не отправлена. Попробуйте ещё раз.',
      });
    } finally {
      loading.value = false;
    }
  };

  const onSheet = () => {
    formStart = false;
    yandexTrackingService.setTrackingGoal('assistantSalesSheetOpen', 'click', {
      placement: 'main_page',
      card_id: cardId,
    });
  };

  watch(model, () => {
    if ((model.value.name || model.value.phone) && !formStart) {
      yandexTrackingService.setTrackingGoal('assistantSalesFormStart', 'click', {
        placement: 'main_page',
        card_id: cardId,
      });
      formStart = true;
    }
  });

  useIntersectionObserver(
    targetEl,
    ([entry]) => {
      if (entry?.isIntersecting) {
        yandexTrackingService.setTrackingGoal('assistantSalesCardImpression', 'other', {
          placement: 'main_page',
          card_id: cardId,
        });
      }
    },
    { threshold: 0.5 },
  );
</script>

<template>
  <LeadBanner
    ref="formRef"
    v-model="model"
    appearance="compact"
    :title="title"
    :subtitle="subtitle"
    :user-agreement-url="appConfig.legalDocuments.user_agreement"
    :privacy-policy-url="appConfig.legalDocuments.privacy"
    :data-processing-url="appConfig.legalDocuments.personal_data_agreement"
    :is-submitted="isSubmitted"
    :loading="loading"
    :mobile="mobile"
    :modal-title="modalTitle"
    :button-text="LEAD_BANNER_TEXTS.submitButton"
    class="lead-banner"
    @sheetOpen="onSheet"
    @submit="onSubmit"
  ></LeadBanner>
</template>

<style lang="scss" scoped>
  .lead-banner {
    ::v-deep .tl-button {
      background: transparent;
      border: 1px solid var(--color-text-inverse);
    }
  }
</style>
